class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.assignSellingPlanAddToCartListeners();
  }

  assignSellingPlanAddToCartListeners() {
    this.sellingPlanAddToCartButtons = this.querySelectorAll('.selling-plan-add-to-cart');
    this.sellingPlanAddToCartButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.addSellingPlanToCart(button);
      });
    });
  }

  addSellingPlanToCart(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');
    button.querySelector('.loading__spinner').classList.remove('hidden');

    const line = parseInt(button.dataset.line);
    const sellingPlanId = button.dataset.sellingPlanId;
    const variantId = button.dataset.variantId;

    // Use cart change endpoint to update the selling plan on existing line item
    const body = JSON.stringify({
      id: variantId,
      quantity: button.dataset.itemCurrentQuantity,
      selling_plan: sellingPlanId !== 'single-purchase' ? sellingPlanId : null,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    this.closest('cart-drawer').setActiveElement(document.activeElement);

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-drawer',
            productVariantId: button.dataset.variantId,
            errors: response.errors || response.description,
            message: response.message,
          });

          const soldOutMessage = button.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          button.setAttribute('aria-disabled', true);
          button.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          return;
        }

        // Update the cart sections with the new data
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            response.sections[section.section],
            section.selector
          );
        });

        // Re-assign event listeners to the new buttons
        this.assignSellingPlanAddToCartListeners();

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: response, variantId: variantId });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.querySelector('.loading__spinner').classList.add('hidden');
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
